import { App as AntApp } from "antd";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";

export type PortalVisibilityItem = { type: "hotel" | "activity" | "transport"; id: string };

/** Confirms inventory hiding when it would block currently shared itinerary templates. */
export const usePortalVisibilityConfirmation = () => {
  const { modal } = AntApp.useApp();
  const queryClient = useQueryClient();

  const confirmHiding = async (items: PortalVisibilityItem[]): Promise<boolean> => {
    if (!items.length) return true;
    const response = await axios.post<{
      affectedCount: number;
      templates: Array<{ id: string; name: string }>;
    }>("/api/itinerary-templates/portal-visibility-impact", { items });
    if (!response.data.affectedCount) return true;
    const names = response.data.templates.slice(0, 5).map((template) => template.name).join(", ");
    const remainder = Math.max(0, response.data.affectedCount - 5);
    return new Promise<boolean>((resolve) => {
      modal.confirm({
        title: "Hide inventory from agencies?",
        content: `${response.data.affectedCount} shared ${response.data.affectedCount === 1 ? "template" : "templates"} will become unavailable on the portal: ${names}${remainder ? ` and ${remainder} more` : ""}.`,
        okText: "Hide from agencies",
        okType: "danger",
        cancelText: "Keep visible",
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const invalidateVisibilityQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["itinerary-templates"] }),
      queryClient.invalidateQueries({ queryKey: ["itinerary-template"] }),
      queryClient.invalidateQueries({ queryKey: ["itinerary-template-recommendations"] }),
      queryClient.invalidateQueries({ queryKey: ["paginated-hotels"] }),
      queryClient.invalidateQueries({ queryKey: ["hotel"] }),
      queryClient.invalidateQueries({ queryKey: ["list-hotels"] }),
      queryClient.invalidateQueries({ queryKey: ["infinite-hotels"] }),
      queryClient.invalidateQueries({ queryKey: ["quick-search-hotels"] }),
      queryClient.invalidateQueries({ queryKey: ["paginated-activities"] }),
      queryClient.invalidateQueries({ queryKey: ["activity"] }),
      queryClient.invalidateQueries({ queryKey: ["list-activities"] }),
      queryClient.invalidateQueries({ queryKey: ["infinite-activities"] }),
      queryClient.invalidateQueries({ queryKey: ["quick-search-activities"] }),
      queryClient.invalidateQueries({ queryKey: ["paginated-transports"] }),
      queryClient.invalidateQueries({ queryKey: ["transport"] }),
      queryClient.invalidateQueries({ queryKey: ["list-transports"] }),
      queryClient.invalidateQueries({ queryKey: ["infinite-transports"] }),
      queryClient.invalidateQueries({ queryKey: ["quick-search-transports"] }),
    ]);
  };

  return { confirmHiding, invalidateVisibilityQueries };
};
